// src/App.js
import React, { useRef, useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { TypeAnimation } from "react-type-animation";
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { GraduationCap, BookOpen, Trophy, Code2, Globe, Database, Users, Mail, Phone, MapPin, Download, ChevronDown, Sparkles, ExternalLink, Sun, Moon, X, Filter, ChevronUp } from "lucide-react";
import emailjs from '@emailjs/browser';
import { FaGithub, FaLinkedin, FaTwitter, FaInstagram } from "react-icons/fa";

// Lazy load heavy components
const ParticlesComponent = lazy(() => import("./components/ParticlesBackground"));
const ProjectModal = lazy(() => import("./components/ProjectModal"));

// Helper: dynamic image import (for static assets)
const getImagesFromFolder = (folderPath) => {
  try {
    const context = require.context(folderPath, false, /\.(png|jpe?g|svg)$/);
    return context.keys().map(context);
  } catch {
    return [];
  }
};

// Enhanced projects data with preview images
const projects = [
  {
    title: "Vigneshwara Power Controls",
    desc: "Developed a modern business website showcasing services, products, and company profile with a responsive and dynamic UI.",
    tech: ["React", "HTML", "CSS", "JavaScript", "PHP"],
    live: "https://vigneshwarapowercontrols.in",
  },
  {
    title: "Luminous Cloud DBA",
    desc: "Built a scalable cloud-based database platform with multi-tenant architecture and integrated analytics dashboard.",
    tech: ["HTML", "CSS", "JavaScript", "PHP", "MySQL"],
    live: "https://luminousclouddb.com",
  },
  {
    title: "Crazy Mobiles",
    desc: "Developed an e-commerce website for mobile sales featuring product listings, responsive UI, and user-friendly navigation.",
    tech: ["HTML", "CSS", "JavaScript", "PHP", "MySQL"],
    live: "https://crazymobiles.in",
  },
  {
    title: "PROCO EdTech Platform",
    desc: "Designed and developed a full-stack EdTech platform enabling course delivery, student engagement, and administrative management.",
    tech: ["HTML", "CSS", "JavaScript", "PHP", "MySQL"],
    live: "https://proco.co.in",
  },
  {
    title: "PROCO LMS Platform",
    desc: "Engineered a scalable Learning Management System (LMS) with course management, student tracking, and performance analytics features.",
    tech: ["HTML", "CSS", "JavaScript", "PHP", "MySQL"],
    live: "https://www.proco.rf.gd",
  },
  {
    title: "Venkateswara Enterprises",
    desc: "Created a clean and responsive business website for industrial products and services with optimized performance.",
    tech: ["HTML", "CSS", "JavaScript", "PHP"],
    live: "https://venkateswaraenterprises.in",
  }
];

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return <div className="p-10 text-center">Something went wrong. Please refresh the page.</div>;
    }
    return this.props.children;
  }
}

// Main App Component
function App() {
  // Refs for smooth scroll
  const sections = {
    home: useRef(null),
    about: useRef(null),
    education: useRef(null),
    skills: useRef(null),
    projects: useRef(null),
    experience: useRef(null),
    certificates: useRef(null),
    academic: useRef(null),
    awards: useRef(null),
    contact: useRef(null),
  };

  const scrollTo = useCallback((ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("theme");
      if (stored) return stored === "dark";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  // Load PROCO images for experience section
  const [procoImages, setProcoImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadProcoImages = () => {
      try {
        const context = require.context("./assets/proco", false, /\.(png|jpe?g|svg|webp)$/);
        const images = context.keys().map(context);
        setProcoImages(images);
      } catch {
        setProcoImages([]);
      }
    };
    loadProcoImages();
  }, []);

  // Auto‑rotate images every 4 seconds
  useEffect(() => {
    if (procoImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % procoImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [procoImages.length]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  const [currentAcademicIndex, setCurrentAcademicIndex] = useState(0);
  const [isCertificateRailPaused, setIsCertificateRailPaused] = useState(false);

  // Load dynamic images
  const [certImages, setCertImages] = useState([]);
  const [featuredCertImages, setFeaturedCertImages] = useState([]);
  const [otherCertImages, setOtherCertImages] = useState([]);

  const importAll = (r) => r.keys().map(r);

  const academicImages = importAll(
    require.context("./assets/topper", false, /\.(png|jpe?g|webp)$/)
  );

  // Load featured certificates from special subfolder
  useEffect(() => {
    const loadFeaturedCerts = () => {
      try {
        const context = require.context("./assets/certificates/special", false, /\.(png|jpe?g|svg|webp)$/);
        const images = context.keys().map(context);
        setFeaturedCertImages(images);
      } catch {
        setFeaturedCertImages([]);
      }
    };
    loadFeaturedCerts();
  }, []);

  // Load other certificates from main certificates folder (excluding special subfolder)
  useEffect(() => {
    const loadOtherCerts = () => {
      try {
        const context = require.context("./assets/certificates", false, /\.(png|jpe?g|svg|webp)$/);
        const images = context.keys().map(context);
        setOtherCertImages(images);
      } catch {
        setOtherCertImages([]);
      }
    };
    loadOtherCerts();
  }, []);

  const certificateSlides = useMemo(() => {
    const slides = [];
    for (let index = 0; index < otherCertImages.length; index += 3) {
      slides.push(otherCertImages.slice(index, index + 3));
    }
    return slides;
  }, [otherCertImages]);
  const marqueeAcademicImages = useMemo(() => [...academicImages, ...academicImages], [academicImages]);

  const awardVideos = [
    require("./assets/vid2.mp4"),
    require("./assets/vid1.mp4"),
  ];

  // Project filtering
  const [activeFilter, setActiveFilter] = useState("all");
  const allTechTags = useMemo(() => {
    const tags = new Set();
    projects.forEach(p => p.tech.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, []);
  
  const filteredProjects = useMemo(() => {
    if (activeFilter === "all") return projects;
    return projects.filter(p => p.tech.includes(activeFilter));
  }, [activeFilter]);

  // Modal state
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  useEffect(() => {
    if (academicImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentAcademicIndex((prev) => (prev + 1) % academicImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [academicImages.length]);

  const [activeCertificateSlide, setActiveCertificateSlide] = useState(0);

  useEffect(() => {
    if (activeCertificateSlide >= certificateSlides.length) {
      setActiveCertificateSlide(0);
    }
  }, [activeCertificateSlide, certificateSlides.length]);

  useEffect(() => {
    if (isCertificateRailPaused || certificateSlides.length <= 1) return;
    const interval = setInterval(() => {
      setActiveCertificateSlide((current) => (current + 1) % certificateSlides.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [certificateSlides.length, isCertificateRailPaused]);

  // Contact form
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const sendEmail = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setSubmitStatus(null);

  const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  if (!serviceId || !templateId || !publicKey) {
    console.error("EmailJS credentials missing");
    setSubmitStatus("error");
    setIsSubmitting(false);
    // Redirect to WhatsApp after a short delay
    setTimeout(() => {
      redirectToWhatsApp();
    }, 1500);
    return;
  }

  try {
    await emailjs.send(serviceId, templateId, {
      from_name: formData.name,
      from_email: formData.email,
      message: formData.message,
    }, publicKey);
    setSubmitStatus("success");
    setFormData({ name: "", email: "", message: "" });
  } catch (error) {
    console.error("EmailJS error:", error);
    setSubmitStatus("error");
    // Redirect to WhatsApp on failure
    setTimeout(() => {
      redirectToWhatsApp();
    }, 1500);
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 5000);
  }
};

// Helper function to redirect to WhatsApp with form data
const redirectToWhatsApp = () => {
  const phoneNumber = "+916304246124"; // same as displayed in contact
  const message = `Name: ${formData.name}%0AEmail: ${formData.email}%0AMessage: ${formData.message}`;
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
  window.location.href = whatsappUrl;
};

  // Mouse move effect for hero (only on desktop)
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const rect = heroRef.current?.getBoundingClientRect();
      if (rect) {
        mouseX.set(e.clientX - rect.left);
        mouseY.set(e.clientY - rect.top);
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  const spotlightX = useSpring(mouseX, { stiffness: 150, damping: 20 });
  const spotlightY = useSpring(mouseY, { stiffness: 150, damping: 20 });
  const rotateX = useTransform(mouseY, [0, 400], [10, -10]);
  const rotateY = useTransform(mouseX, [0, 600], [-10, 10]);

  // Scroll to top button visibility
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Animation variants (respect reduced motion)
  const prefersReducedMotion = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    return false;
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <ErrorBoundary>
      <div className="portfolio-shell bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 font-sans antialiased overflow-x-hidden transition-colors duration-300">
        {/* Custom CSS for animations */}
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes blob {
            0%, 100% { transform: translate(0px, 0px) scale(1); }
            33% { transform: translate(30px, -20px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
          }
          .animate-marquee {
            animation: marquee 72s linear infinite;
            will-change: transform;
          }
          .portfolio-shell {
            --tech-ink: #07111f;
            --tech-surface: #0d1b2a;
            --tech-surface-raised: #10243a;
            --tech-border: #1d3b5d;
            --tech-blue: #38bdf8;
            --tech-blue-strong: #2563eb;
            --tech-text: #eaf6ff;
            background: #f7fbff;
          }
          .dark .portfolio-shell { background: var(--tech-ink); color: var(--tech-text); }
          .portfolio-shell .bg-gradient-to-r,
          .portfolio-shell .bg-gradient-to-br,
          .portfolio-shell .bg-gradient-to-b {
            background-image: linear-gradient(135deg, #0ea5e9, #2563eb) !important;
          }
          .dark .portfolio-shell .bg-white,
          .dark .portfolio-shell .bg-white\\/80,
          .dark .portfolio-shell .bg-white\\/70,
          .dark .portfolio-shell .bg-white\\/60,
          .dark .portfolio-shell .dark\\:bg-gray-800,
          .dark .portfolio-shell .dark\\:bg-gray-800\\/80 {
            background-color: var(--tech-surface) !important;
          }
          .dark .portfolio-shell .dark\\:bg-gray-900,
          .dark .portfolio-shell .dark\\:bg-gray-900\\/50,
          .dark .portfolio-shell .dark\\:bg-gray-900\\/80 {
            background-color: #081625 !important;
          }
          .dark .portfolio-shell .bg-gray-50,
          .dark .portfolio-shell .dark\\:bg-gray-700 { background-color: var(--tech-surface-raised) !important; }
          .dark .portfolio-shell .border-gray-200,
          .dark .portfolio-shell .border-gray-200\\/60,
          .dark .portfolio-shell .dark\\:border-gray-700,
          .dark .portfolio-shell .dark\\:border-gray-700\\/50,
          .dark .portfolio-shell .dark\\:border-gray-700\\/60 { border-color: var(--tech-border) !important; }
          .portfolio-shell .text-indigo-600,
          .portfolio-shell .hover\\:text-indigo-600:hover,
          .dark .portfolio-shell .dark\\:text-indigo-400 { color: var(--tech-blue) !important; }
          .portfolio-shell .bg-indigo-600,
          .portfolio-shell .hover\\:bg-indigo-700:hover { background-color: var(--tech-blue-strong) !important; }
          .portfolio-shell .border-indigo-500,
          .portfolio-shell .border-indigo-200,
          .dark .portfolio-shell .dark\\:border-indigo-800 { border-color: rgba(56, 189, 248, 0.45) !important; }
          .portfolio-shell .bg-indigo-50,
          .portfolio-shell .bg-indigo-100,
          .dark .portfolio-shell .dark\\:bg-indigo-900,
          .dark .portfolio-shell .dark\\:bg-indigo-900\\/50 { background-color: rgba(14, 165, 233, 0.12) !important; }
          .dark .portfolio-shell .text-gray-500,
          .dark .portfolio-shell .dark\\:text-gray-400 { color: #91a9c2 !important; }
          .dark .portfolio-shell .text-gray-700,
          .dark .portfolio-shell .dark\\:text-gray-300 { color: #c8d8e8 !important; }
          .dark .portfolio-shell .text-gray-900 { color: var(--tech-text) !important; }
          .portfolio-backdrop { background: radial-gradient(circle at 20% 0%, #e0f2fe 0%, transparent 30%), #f7fbff !important; }
          .dark .portfolio-backdrop { background: radial-gradient(circle at 15% 5%, rgba(14, 165, 233, 0.15) 0%, transparent 28%), #07111f !important; }
          .certificate-carousel-card {
            transform-origin: center;
            transition: transform 260ms ease, box-shadow 260ms ease, border-color 260ms ease;
          }
          .certificate-carousel-card:hover,
          .certificate-carousel-card:focus-within {
            transform: translateY(-8px) scale(1.045);
            z-index: 20;
            border-color: rgba(56, 189, 248, 0.75) !important;
            box-shadow: 0 24px 45px rgba(2, 132, 199, 0.22);
          }
          @media (max-width: 767px) {
            .certificate-track { animation: none; }
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-marquee { animation: none; }
          }
          .animate-blob {
            animation: blob 12s infinite ease-in-out;
          }
          .animation-delay-2000 { animation-delay: 2s; }
          .animation-delay-4000 { animation-delay: 4s; }
        `}</style>

        {/* Background gradient + particles */}
        <div className="portfolio-backdrop fixed inset-0 -z-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" />
        <div className="fixed inset-0 -z-10 pointer-events-none">
          <Suspense fallback={null}>
            <ParticlesComponent />
          </Suspense>
        </div>

        {/* Navbar */}
        <nav className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent cursor-pointer"
              onClick={() => scrollTo(sections.home)}
            >
              Harsha Naga Sai
            </motion.span>
            <div className="hidden md:flex gap-7 text-sm font-medium">
              {Object.keys(sections).map((key) => (
                <motion.button
                  key={key}
                  onClick={() => scrollTo(sections[key])}
                  className="hover:text-indigo-600 transition-colors capitalize relative group"
                  whileHover={{ y: -2 }}
                >
                  {key === "home" ? "Home" : key}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-indigo-600 transition-all group-hover:w-full" />
                </motion.button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDarkMode(prev => !prev)}
                className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button
                className="md:hidden text-gray-800 dark:text-gray-200 text-2xl focus:outline-none"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
              >
                ☰
              </button>
            </div>
          </div>
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-4 px-6 flex flex-col gap-3 shadow-lg"
              >
                {Object.keys(sections).map((key) => (
                  <button
                    key={key}
                    onClick={() => {
                      scrollTo(sections[key]);
                      setMobileMenuOpen(false);
                    }}
                    className="text-gray-700 dark:text-gray-300 hover:text-indigo-600 capitalize text-left py-2"
                  >
                    {key === "home" ? "Home" : key}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        <main className="max-w-6xl mx-auto px-6 py-16 space-y-16">
          {/* ===== HERO SECTION ===== */}
          <motion.section
            ref={sections.home}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="relative flex flex-col-reverse md:flex-row items-center justify-between gap-12 min-h-[90vh] px-6 md:px-12 lg:px-24 overflow-hidden"
          >
            {/* Animated blobs */}
            <div className="absolute inset-0 -z-20 bg-[radial-gradient(#6366f1_1px,transparent_1px)] [background-size:32px_32px] opacity-20" />
            <motion.div className="absolute top-20 left-10 w-72 h-72 bg-sky-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
            <motion.div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
            <motion.div className="absolute top-1/3 left-1/2 w-60 h-60 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />

            <div className="flex-1 text-center md:text-left space-y-6 z-10 backdrop-blur-sm bg-white/30 dark:bg-gray-900/30 p-6 rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-sm font-medium w-fit mx-auto md:mx-0"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Available for work
              </motion.div>
              <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-xl font-mono text-indigo-600 dark:text-indigo-400">
                Hi, I'm
              </motion.h2>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-5xl md:text-7xl font-extrabold leading-tight bg-gradient-to-r from-sky-400 via-blue-500 to-cyan-300 bg-clip-text text-transparent"
              >
                Harsha Naga Sai P.
              </motion.h1>
              <div className="text-xl md:text-2xl font-semibold text-gray-700 dark:text-gray-200 h-[70px]">
                <TypeAnimation
                  sequence={["Software Engineer 💻", 2000, "Full Stack Developer 🌐", 2000, "Frontend Specialist 🎨", 2000, "Backend Developer ⚙️", 2000, "Problem Solver 🧩", 2000]}
                  speed={50}
                  repeat={Infinity}
                  cursor={true}
                  className="border-r-2 border-indigo-500 pr-2"
                />
              </div>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-gray-600 dark:text-gray-300 max-w-md mx-auto md:mx-0 text-lg">
                Building scalable systems and real-world digital solutions with performance and elegance.
              </motion.p>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="flex flex-wrap gap-2 justify-center md:justify-start">
                {["React", "Node.js", "TypeScript", "Tailwind", "Next.js"].map((tech, i) => (
                  <span key={i} className="px-3 py-1 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 rounded-full border border-indigo-200 dark:border-indigo-800">
                    {tech}
                  </span>
                ))}
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="flex flex-wrap gap-4 justify-center md:justify-start">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => scrollTo(sections.projects)}
                  className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full font-semibold shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 flex items-center gap-2"
                >
                  <Sparkles size={18} /> Explore Work
                </motion.button>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="/Harsha_P.pdf"
                  download="Harsha_Peddoju_Resume.pdf"
                  className="px-8 py-3 bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 rounded-full font-semibold border-2 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-all duration-300 flex items-center gap-2"
                >
                  <Download size={18} /> Resume
                </motion.a>
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="flex gap-4 justify-center md:justify-start pt-4">
                <a href="https://github.com/yourusername" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600 transition text-2xl" aria-label="GitHub">
                  <FaGithub size={24} />
                </a>
                <a href="https://linkedin.com/in/yourusername" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600 transition text-2xl" aria-label="LinkedIn">
                  <FaLinkedin size={24} />
                </a>
                <a href="https://twitter.com/yourusername" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600 transition text-2xl" aria-label="Twitter">
                  <FaTwitter size={24} />
                </a>
              </motion.div>
            </div>

            <div className="flex-1 flex justify-center relative group">
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full opacity-75 blur-md group-hover:opacity-100 transition duration-500"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
                style={{ width: "calc(100% + 10px)", height: "calc(100% + 10px)" }}
              />
              <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
                <motion.img
                  src={require("./assets/profile-v2.png")}
                  alt="Harsha Naga Sai Profile"
                  className="w-64 h-64 md:w-80 md:h-80 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-2xl"
                  onError={(e) => (e.target.src = "https://via.placeholder.com/300?text=Photo")}
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  whileHover={{ scale: 1.02 }}
                />
              </motion.div>
            </div>
            <motion.div
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ delay: 1.2, duration: 1 }}
  className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center cursor-pointer"
  onClick={() => scrollTo(sections.projects)}
>
  <motion.div
    animate={{ y: [0, 12, 0] }}
    transition={{ repeat: Infinity, duration: 1.5 }}
    className="flex flex-col items-center gap-1 text-gray-500 hover:text-indigo-500"
  >
    <span className="text-xs tracking-wide">Scroll</span>
    <ChevronDown size={22} />
  </motion.div>
</motion.div>
</motion.section>

          {/* ===== ABOUT SECTION ===== */}
          <motion.section
            ref={sections.about}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="relative"
          >
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-3xl p-8 md:p-12 shadow-xl">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-indigo-500 pl-4">
                About Me
              </h2>
              <div className="grid md:grid-cols-2 gap-10 items-center">
                <div>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                    I’m a <span className="font-semibold text-gray-900 dark:text-white">Computer Science Engineering student</span> with a current CGPA of <span className="text-indigo-600 font-semibold">8.542</span>, recognized as <span className="text-indigo-600 font-semibold">Academic Topper of the Year 2023</span>. I’m deeply passionate about building scalable software systems, working with data, and leveraging technologies to create impactful digital solutions.
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 mt-4 leading-relaxed">
                    I specialize in building scalable systems, modern web applications, and real-world digital products that combine performance with elegant design.
                  </p>
                  <div className="flex gap-6 mt-6">
                    <div>
                      <h3 className="text-2xl font-bold text-indigo-600">8.542</h3>
                      <p className="text-sm text-gray-500">CGPA</p>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-indigo-600">10+</h3>
                      <p className="text-sm text-gray-500">Projects</p>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-indigo-600">5+</h3>
                      <p className="text-sm text-gray-500">Certifications</p>
                    </div>
                  </div>
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 blur-2xl opacity-20 rounded-2xl group-hover:opacity-30 transition" />
                  <div className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">What I Do</h3>
                    <ul className="space-y-4 text-gray-600 dark:text-gray-400 text-sm">
                      <li className="flex items-center gap-3"><Code2 size={18} className="text-indigo-600" /> Full Stack Development</li>
                      <li className="flex items-center gap-3"><Database size={18} className="text-indigo-600" /> Scalable System Design</li>
                      <li className="flex items-center gap-3"><Globe size={18} className="text-indigo-600" /> UI/UX Focused Interfaces</li>
                      <li className="flex items-center gap-3"><Users size={18} className="text-indigo-600" /> Data & Cloud Solutions</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* ===== EDUCATION SECTION ===== */}
          <motion.section
            ref={sections.education}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="relative"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-10 border-l-4 border-indigo-500 pl-4">
              Education
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: GraduationCap, title: "B.E Computer Science Engineering", score: "CGPA: 8.542", institution: "Saveetha University, Chennai" },
                { icon: BookOpen, title: "Intermediate (Class XII)", score: "821 / 1000", institution: "Sri Chaitanya Junior College, AP" },
                { icon: Trophy, title: "SSC (Class X)", score: "581 / 600", institution: "Sri Chaitanya School, AP" },
              ].map((edu, idx) => (
                <motion.div key={idx} whileHover={{ y: -8, scale: 1.02 }} className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 blur-xl opacity-20 rounded-2xl group-hover:opacity-30 transition" />
                  <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-lg transition">
                    <edu.icon className="w-8 h-8 text-indigo-600 mb-4" />
                    <p className="text-gray-900 dark:text-white font-semibold text-lg">{edu.title}</p>
                    <p className="text-indigo-600 font-medium mt-1">{edu.score}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">{edu.institution}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* ===== SKILLS SECTION ===== */}
          <motion.section
            ref={sections.skills}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="mb-8"
          >
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 border-l-4 border-indigo-500 pl-4">Skills & Expertise</h2>
              <p className="text-gray-500 mt-3 ml-4 max-w-xl">Technologies and tools I use to build scalable and modern applications.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Programming", items: [{ name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" }, { name: "Java", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" }, { name: "JavaScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg" }, { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg" }, { name: "SQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" }] },
                { title: "Web Development", items: [{ name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" }, { name: "HTML5", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg" }, { name: "CSS3", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg" }, { name: "Tailwind", logo: "https://www.vectorlogo.zone/logos/tailwindcss/tailwindcss-icon.svg" }, { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" }] },
                { title: "Tools & Databases", items: [{ name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" }, { name: "Power BI", logo: "https://upload.wikimedia.org/wikipedia/commons/c/cf/New_Power_BI_Logo.svg" }, { name: "Git", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg" }, { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" }, { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" }] },
                { title: "Soft Skills", items: [{ name: "Leadership" }, { name: "Communication" }, { name: "Problem Solving" }, { name: "Team Collaboration" }, { name: "Time Management" }] },
              ].map((group, i) => (
                <motion.div key={i} whileHover={{ y: -6 }} className="bg-white border border-gray-200 rounded-xl p-6 transition hover:shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-5">{group.title}</h3>
                  <div className="flex flex-wrap gap-3">
                    {group.items.map((skill, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-white hover:shadow-sm transition">
                        {skill.logo && <img src={skill.logo} alt={skill.name} className="w-5 h-5" />}
                        <span className="text-sm text-gray-700 font-medium">{skill.name}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* PROJECTS – CLEAN CARDS WITH WORKING BUTTONS */}
          <motion.section
            ref={sections.projects}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="py-20 px-4 md:px-8"
          >
            <div className="max-w-6xl mx-auto">
              <div className="mb-14 text-center">
                <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white">
                  Featured Projects
                  <span className="block h-1 w-20 bg-gradient-to-r from-indigo-500 to-purple-500 mx-auto mt-3 rounded-full"></span>
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-4 text-lg">
                  Click the button to visit the live project
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {projects.map((project, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -8 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col h-full"
                  >
                    <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                        {project.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-5 flex-grow">
                        {project.desc}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {project.tech.map((tech) => (
                          <span key={tech} className="text-xs px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                            {tech}
                          </span>
                        ))}
                      </div>
                      {project.live && project.live !== "#" ? (
                        <a
                          href={project.live}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                        >
                          Live Preview →
                        </a>
                      ) : (
                        <button disabled className="inline-flex items-center justify-center w-full py-3 px-4 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-semibold rounded-xl cursor-not-allowed">
                          Coming Soon
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* ===== EXPERIENCE SECTION ===== */}
          <motion.section
            ref={sections.experience}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-indigo-500 pl-4">
              Experience
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* LEFT SIDE – Text content */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 md:p-8">
                <div className="flex flex-wrap justify-between items-start border-b border-gray-100 dark:border-gray-700 pb-4 mb-4">
                  <h3 className="text-gray-900 dark:text-white text-2xl font-semibold">Founder</h3>
                  <span className="text-gray-500 text-sm">Dec 2024 – Jan 2026</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <p className="text-indigo-600 font-medium text-lg">PROCO EdTech Platform</p>
                  <div className="flex gap-3">
                    <a href="https://proco.co.in" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600 transition-all hover:scale-110">
                      <Globe size={18} />
                    </a>
                    <a href="https://www.linkedin.com/company/procoindia" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600 transition-all hover:scale-110">
                      <FaLinkedin size={18} />
                    </a>
                    <a href="https://www.instagram.com/proco_india" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600 transition-all hover:scale-110">
                      <FaInstagram size={18} />
                    </a>
                  </div>
                </div>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 text-sm mt-4 space-y-2 leading-relaxed">
                  <li>Founded and scaled an EdTech platform, designing and developing the complete full-stack architecture.</li>
                  <li>Led end-to-end operations including instructor onboarding, course management, and platform strategy.</li>
                  <li>Achieved 200% growth in student engagement through optimized user experience and targeted initiatives.</li>
                  <li>Built automation systems for workflows, analytics, and performance tracking to improve efficiency.</li>
                  <li>Managed and mentored a remote team of developers and content creators to deliver high-quality learning experiences.</li>
                </ul>
              </div>

              {/* RIGHT SIDE – Image carousel */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 md:p-6 flex flex-col items-center justify-center">
                <div className="relative w-full bg-gray-100 dark:bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: "280px", maxHeight: "400px" }}>
                  {procoImages.length > 0 ? (
                    <img
                      src={procoImages[currentImageIndex]}
                      alt={`PROCO snapshot ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain transition-opacity duration-500"
                      style={{ maxHeight: "380px" }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 p-8 text-center">
                      📸 No images found in assets/proco/
                    </div>
                  )}
                </div>
                {procoImages.length > 0 && (
                  <div className="flex gap-2 mt-4">
                    {procoImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          idx === currentImageIndex ? "w-6 bg-indigo-600" : "w-2 bg-gray-300 dark:bg-gray-600 hover:bg-indigo-400"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.section>

          {/* ===== CERTIFICATES SECTION ===== */}
          <motion.section
            ref={sections.certificates}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="py-16 px-4 md:px-8 relative"
          >
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-indigo-50/20 to-white dark:from-gray-900 dark:via-indigo-900/5 dark:to-gray-900" />
            <div className="max-w-6xl mx-auto">
              <div className="mb-12 text-center md:text-left">
                <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white inline-block">
                  Certifications
                  <span className="block h-1 w-20 bg-gradient-to-r from-indigo-500 to-purple-500 mt-3 rounded-full mx-auto md:mx-0"></span>
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-4 text-lg max-w-2xl">
                  Industry‑recognized credentials & achievements
                </p>
              </div>

              {/* FEATURED CERTIFICATIONS */}
              {featuredCertImages.length > 0 && (
                <div className="mb-20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-7 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full" />
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Featured Certifications</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">Premium</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {featuredCertImages.map((img, idx) => (
                      <button key={idx} type="button" onClick={() => setSelectedCertificate(img)} className="group relative w-full text-left bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden border border-gray-200/60 dark:border-gray-700/60 focus:outline-none focus:ring-2 focus:ring-sky-400" aria-label={`Open featured certification ${idx + 1}`}>
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-700 bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 pointer-events-none" />
                        <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-6">
                          <img src={img} alt={`Featured certification ${idx + 1}`} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                        </div>
                        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md text-white text-xs px-2 py-1 rounded-full">✨ Featured</div>
                        <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* OTHER CERTIFICATIONS */}
              {otherCertImages.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-7 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full" />
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Other Certifications</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{otherCertImages.length} items</span>
                  </div>
                  <div className="hidden md:block overflow-hidden py-5" onMouseEnter={() => setIsCertificateRailPaused(true)} onMouseLeave={() => setIsCertificateRailPaused(false)}>
                    <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${activeCertificateSlide * 100}%)` }}>
                      {certificateSlides.map((slide, slideIndex) => (
                        <div key={slideIndex} className="min-w-full grid grid-cols-3 gap-6 px-1">
                          {slide.map((img, idx) => (
                            <div key={img} className="certificate-carousel-card bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-md border border-gray-200/60 dark:border-gray-700/60 overflow-hidden group/item" onMouseEnter={() => setIsCertificateRailPaused(true)} onMouseLeave={() => setIsCertificateRailPaused(false)}>
                              <div className="aspect-[4/3] p-5 flex items-center justify-center bg-gray-50 dark:bg-gray-900/50">
                                <img src={img} alt={`Certificate ${slideIndex * 3 + idx + 1}`} className="w-full h-full object-contain transition-transform duration-300 group-hover/item:scale-105" loading="lazy" />
                              </div>
                              <div className="h-1 w-full bg-gradient-to-r from-indigo-500 to-sky-400" />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    {certificateSlides.length > 1 && (
                      <div className="mt-5 flex justify-center gap-2">
                        {certificateSlides.map((_, index) => (
                          <button key={index} type="button" onClick={() => setActiveCertificateSlide(index)} className={`h-2 rounded-full transition-all ${index === activeCertificateSlide ? "w-7 bg-sky-400" : "w-2 bg-slate-500 hover:bg-sky-300"}`} aria-label={`Show certificate group ${index + 1}`} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:hidden">
                    {otherCertImages.map((img, idx) => (
                      <div key={img} className="bg-white/80 dark:bg-gray-800/80 rounded-xl border border-gray-200/60 dark:border-gray-700/60 p-4">
                        <div className="aspect-[4/3] flex items-center justify-center bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                          <img src={img} alt={`Certificate ${idx + 1}`} className="w-full h-full object-contain" loading="lazy" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fallback */}
              {featuredCertImages.length === 0 && otherCertImages.length === 0 && (
                <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-12 text-center border-2 border-dashed border-gray-300 dark:border-gray-700">
                  <div className="text-6xl mb-4">📜</div>
                  <p className="text-gray-600 dark:text-gray-400 font-medium">No certificates found</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    Add featured certificates to <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">src/assets/certificates/special/</code><br />
                    Add other certificates to <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">src/assets/certificates/</code>
                  </p>
                </div>
              )}
            </div>
          </motion.section>

          {/* ===== ACADEMIC TOPPER SECTION ===== */}
          <motion.section
            ref={sections.academic}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-indigo-500 pl-4">
              Academic Achievement
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* LEFT – IMAGES */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 md:p-6 flex flex-col items-center justify-center">
                {academicImages.length > 0 ? (
                  <>
                    <div className="relative w-full bg-gray-100 dark:bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: "280px", maxHeight: "400px" }}>
                      <img
                        src={academicImages[currentAcademicIndex]}
                        alt="Academic Topper"
                        className="w-full h-full object-contain transition-all duration-500"
                        style={{ maxHeight: "380px" }}
                      />
                    </div>
                    {academicImages.length > 1 && (
                      <div className="flex gap-2 mt-4">
                        {academicImages.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentAcademicIndex(idx)}
                            className={`h-2 rounded-full transition-all duration-300 ${
                              idx === currentAcademicIndex ? "w-6 bg-indigo-600" : "w-2 bg-gray-300 dark:bg-gray-600"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 p-8 text-center">
                    📸 Add images to src/assets/topper/
                  </div>
                )}
              </div>

              {/* RIGHT – CONTENT */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 md:p-8">
                <span className="inline-block px-3 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-xs font-semibold rounded-full mb-3">
                  Academic Topper • 2023
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                  Top Performer – Computer Science Engineering
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  Recognized as the highest-performing student across the entire Computer Science Engineering cohort for outstanding academic consistency, technical excellence, and leadership contributions.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm font-medium">🏆 Rank #1</span>
                  <span className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm font-medium">🎓 CSE Department</span>
                  <span className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm font-medium">⭐ Academic Excellence</span>
                </div>
              </div>
            </div>
          </motion.section>

          {/* ===== AWARDS SECTION ===== */}
          <motion.section
            ref={sections.awards}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-indigo-500 pl-4">Awards & Achievements</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {awardVideos.map((src, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                  <video controls autoPlay={idx === 0} muted loop className="w-full aspect-video object-cover" playsInline>
                    <source src={src} type="video/mp4" />
                  </video>
                  <div className="p-4 text-center bg-white dark:bg-gray-800">
                    <p className="text-gray-800 dark:text-gray-200 font-medium">{idx === 0 ? "Founder's Day 2023" : "Agnito 2023"}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* ===== CONTACT SECTION ===== */}
          <motion.section
            ref={sections.contact}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="pb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-indigo-500 pl-4">Let's Connect</h2>
            <div className="grid md:grid-cols-2 gap-10">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <Mail className="text-3xl text-indigo-500 mb-4" />
                <h3 className="text-gray-900 dark:text-white text-2xl font-semibold mb-3">Get in touch</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">I'm currently open for freelance or full‑time opportunities. Reach out and let's build something great.</p>
                <div className="space-y-4">
                  <p className="flex items-center gap-3">
                    <Mail size={18} className="text-indigo-500" />
                    <a href="mailto:harshanagasaipeddoju@gmail.com" className="text-gray-700 dark:text-gray-300 hover:text-indigo-600">harshanagasaipeddoju@gmail.com</a>
                  </p>
                  <p className="flex items-center gap-3">
                    <Phone size={18} className="text-indigo-500" />
                    <a href="tel:+916304246124" className="text-gray-700 dark:text-gray-300 hover:text-indigo-600">+91 6304246124</a>
                  </p>
                  <p className="flex items-center gap-3">
                    <MapPin size={18} className="text-indigo-500" /> Chennai, India
                  </p>
                </div>
                <div className="flex gap-4 mt-8">
                  <a href="https://github.com/yourusername" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600" aria-label="GitHub"><FaGithub size={18} /></a>
                  <a href="https://linkedin.com/in/yourusername" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600" aria-label="LinkedIn"><FaLinkedin size={18} /></a>
                  <a href="https://twitter.com/yourusername" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600" aria-label="Twitter"><FaTwitter size={18} /></a>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <form onSubmit={sendEmail} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email address</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                    <textarea id="message" name="message" rows="5" value={formData.message} onChange={handleChange} required className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 dark:text-white"></textarea>
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold transition disabled:opacity-50">
                    {isSubmitting ? "Sending..." : "Send Message →"}
                  </button>
                  {submitStatus === "success" && (
                    <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 text-sm text-center">
                      Thank you! Your message has been sent.
                    </div>
                  )}
                  {submitStatus === "error" && (
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm text-center">
                      Oops! Something went wrong. Please try again.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </motion.section>
        </main>

        <footer className="relative border-t border-gray-200 dark:border-gray-800 mt-0">

  {/* Gradient background */}
  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 -z-10" />

  <div className="max-w-6xl mx-auto px-6 py-12">

    {/* TOP CTA */}
    <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10">
      <div>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Let’s build something great 🚀
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Open for opportunities, freelance projects, and collaborations.
        </p>
      </div>

      <button
        onClick={() => scrollTo(sections.contact)}
        className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full font-semibold shadow-lg hover:scale-105 transition"
      >
        Contact Me
      </button>
    </div>

    {/* MAIN GRID */}
    <div className="grid md:grid-cols-3 gap-8 mb-10">

      {/* Brand */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Harsha Naga Sai
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Building scalable digital products & real-world solutions with modern technologies.
        </p>
      </div>

      {/* Quick Links */}
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
          Quick Links
        </h4>
        <div className="flex flex-col gap-2 text-sm">
          {["home", "about", "projects", "experience", "contact"].map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(sections[item])}
              className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 text-left capitalize transition"
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {/* Social */}
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
          Connect
        </h4>
        <div className="flex gap-4 text-xl">
          <a href="https://github.com/yourusername" target="_blank" className="hover:text-indigo-600 transition">
            <FaGithub />
          </a>
          <a href="https://linkedin.com/in/yourusername" target="_blank" className="hover:text-indigo-600 transition">
            <FaLinkedin />
          </a>
          <a href="https://instagram.com/proco_india" target="_blank" className="hover:text-indigo-600 transition">
            <FaInstagram />
          </a>
        </div>
      </div>

    </div>

    {/* Divider */}
    <div className="border-t border-gray-200 dark:border-gray-700 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">

      <p className="text-sm text-gray-500 dark:text-gray-400">
        © {new Date().getFullYear()} Harsha Naga Sai Peddoju. All rights reserved.
      </p>

     

    </div>

  </div>
</footer>

        {/* Scroll to top button */}
        <AnimatePresence>
          {showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="fixed bottom-6 right-6 p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 z-50"
              aria-label="Scroll to top"
            >
              <ChevronUp size={20} />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Project Modal (Lazy) */}
        <Suspense fallback={null}>
          <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
        </Suspense>

        <AnimatePresence>
          {selectedCertificate && (
            <motion.div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedCertificate(null)}
              role="dialog"
              aria-modal="true"
              aria-label="Featured certificate preview"
            >
              <motion.div
                className="relative max-h-[90vh] max-w-5xl overflow-hidden rounded-2xl border border-sky-400/40 bg-slate-900 p-3 shadow-2xl"
                initial={{ opacity: 0, scale: 0.94, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 16 }}
                onClick={(event) => event.stopPropagation()}
              >
                <button type="button" onClick={() => setSelectedCertificate(null)} className="absolute right-5 top-5 z-10 rounded-full bg-slate-950/80 p-2 text-white transition hover:bg-sky-500" aria-label="Close certificate preview">
                  <X size={20} />
                </button>
                <img src={selectedCertificate} alt="Featured certificate preview" className="max-h-[84vh] w-auto max-w-full rounded-xl object-contain" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ErrorBoundary>
  );
}

export default App;
